import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { ConfiguracionRolService } from '../configuracion-rol/configuracion-rol.service';

/** Clase que el kit espera en el elemento raiz para emitir el perfil 3.0. */
const CLASE_PERFIL_30 = 'ui-3-0';

/**
 * Perfil de identidad visual del ERP.
 *
 * Nebula no pinta dos disenos a la vez: pinta el suyo de siempre y, cuando la
 * compañia lo pide, le superpone el perfil del manual propuesto. Quien lo pide es
 * el parametro `APARIENCIA` de masters (HT_ARQ_012): con varios roles gana el de
 * mayor prioridad, y sin rol que diga nada vale lo de la compañia. El valor `UI_3.0` cuelga una clase del elemento raiz; el kit
 * tiene bajo esa clase su segundo juego de tokens.
 *
 * El valor viaja por el almacenamiento local y no por la red en el momento de
 * pintar: al arrancar se aplica lo ultimo que se supo, y el valor vigente se
 * consulta despues, para la proxima vez. Un cambio hecho por el administrador se
 * ve, por tanto, en la siguiente sesion del usuario, y nunca como un cambio de
 * aspecto a mitad de sesion.
 */
@Injectable({ providedIn: 'root' })
export class UiProfileService {
  private readonly configuracion = inject(ConfiguracionRolService);
  private readonly auth = inject(AuthService);

  /** Clave del almacenamiento local donde viaja el perfil entre sesiones. */
  private static readonly STORAGE_KEY = 'ui_profile';

  /** Perfil por defecto: lo que se guarda cuando masters responde sin perfil configurado. */
  private static readonly PROFILE_DEFAULT = 'UI_DEFAULT';

  /** Unico valor que cambia el aspecto; cualquier otro deja el perfil vigente. */
  private static readonly PROFILE_VALUE = 'UI_3.0';

  /** Clase que el kit espera en el elemento raiz para emitir el perfil. */
  private static readonly PROFILE_CLASS = CLASE_PERFIL_30;

  /**
   * Pone el perfil en marcha: aplica lo guardado y deja pedido lo vigente.
   *
   * Corre una sola vez, en el arranque. La consulta se engancha a la sesion y no
   * al arranque porque al iniciar sesion la aplicacion no se recarga: sin eso, un
   * perfil recien cambiado tardaria dos sesiones en llegar en vez de una.
   */
  start(): void {
    this.applyFromStorage();

    this.auth.isAuthenticated$
      .pipe(
        filter((autenticado) => autenticado),
        switchMap(() => this.refresh()),
      )
      .subscribe();
  }

  /**
   * Aplica al documento el perfil que quedo guardado de la sesion anterior.
   *
   * Es sincrono y no toca la red a proposito: corre en el arranque, antes del
   * primer pintado, de modo que la pagina nunca se ve primero con un aspecto y
   * luego con otro.
   */
  applyFromStorage(): void {
    this.apply(this.stored() === UiProfileService.PROFILE_VALUE);
  }

  /**
   * Consulta el perfil de la empresa y lo aplica YA, sin esperar al proximo arranque.
   *
   * Se usa en los dos momentos en que el usuario esta esperando de todas formas:
   * al entrar y al elegir compania. Antes esto no se hacia por una razon buena -la
   * pagina no debe verse primero de una manera y luego de otra-, pero esos dos
   * momentos ocurren DETRAS del aviso de preparacion, de modo que el cambio no se
   * ve. Sin esto, quien entraba por primera vez -o tras limpiar el navegador- veia
   * el perfil vigente y tenia que recargar para que apareciera el suyo.
   *
   * El perfil es de la COMPANIA, asi que aqui vale lo que diga la que este activa
   * en ese instante; al elegir otra se vuelve a llamar.
   */
  aplicarVigente(): Observable<void> {
    return this.refresh().pipe(tap(() => this.applyFromStorage()));
  }

  /**
   * Consulta el perfil vigente de la empresa y lo guarda para la proxima sesion.
   *
   * No aplica nada: lo que trae vale a partir del siguiente arranque. Nunca
   * propaga error — si el maestro no responde, el perfil guardado sigue siendo
   * el bueno y la aplicacion no se entera.
   */
  refresh(): Observable<void> {
    // El perfil sale del parametro APARIENCIA de masters (HT_ARQ_012): con varios roles gana el
    // de mayor prioridad y, si ningun rol dice nada, vale lo de la compañia y luego el defecto. Si masters no responde, el perfil guardado sigue siendo el bueno.
    return this.configuracion.cargar().pipe(
      tap(() => this.store(this.configuracion.uiProfile() || UiProfileService.PROFILE_DEFAULT)),
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  /**
   * Indica si el perfil 3.0 esta puesto en el documento.
   *
   * Lo pregunta al elemento raiz y no al almacenamiento a proposito: lo que vale
   * para pintar es lo que se aplico en el arranque, porque el valor guardado
   * puede ser ya el de la proxima sesion. Quien necesite decidir en tiempo de
   * pintado pregunta aqui y no al `classList` por su cuenta, para que el perfil
   * siga teniendo un solo dueno.
   */
  esPerfil30(): boolean {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains(UiProfileService.PROFILE_CLASS);
  }

  /** Perfil guardado, o nulo si aun no se sabe. */
  private stored(): string | null {
    try {
      return localStorage.getItem(UiProfileService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(valor: string | null): void {
    try {
      if (valor) {
        localStorage.setItem(UiProfileService.STORAGE_KEY, valor);
      } else {
        localStorage.removeItem(UiProfileService.STORAGE_KEY);
      }
    } catch {
      // Sin almacenamiento el perfil simplemente no persiste entre sesiones.
    }
  }

  private apply(activo: boolean): void {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle(UiProfileService.PROFILE_CLASS, activo);
  }
}

/** Clases de boton de las acciones de pagina; `text` es el de solo texto (Cancelar, Volver). */
export type VarianteBoton = 'primary' | 'secondary' | 'text' | 'danger';

/**
 * Elige un valor segun el perfil visual aplicado: uno para el perfil 3.0 y otro para el perfil por
 * defecto, que asi queda exactamente como estaba.
 *
 * <p>Sirve para lo que el perfil 3.0 decide distinto sin tocar el otro perfil, como la jerarquia de
 * botones (orden del arquitecto, 1-oct-2026): Cancelar y Volver pasan a solo texto, Exportar a
 * tonal y Anular a peligro solo en el 3.0.</p>
 *
 * @param enPerfil30 el valor del perfil 3.0.
 * @param porDefecto el valor del perfil por defecto.
 * @returns el que corresponde al perfil aplicado.
 */
export function porPerfil<T>(enPerfil30: T, porDefecto: T): T {
  const perfil30 =
    typeof document !== 'undefined' && document.documentElement.classList.contains(CLASE_PERFIL_30);
  return perfil30 ? enPerfil30 : porDefecto;
}
